import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Camera, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Upload, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE = process.env.REACT_APP_BACKEND_URL;

// Fullscreen Lightbox with pinch-to-zoom, swipe navigation, and drag
const Lightbox = ({ images, currentIndex, onClose }) => {
  const [idx, setIdx] = useState(currentIndex);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [lastDist, setLastDist] = useState(0);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [swipeStartX, setSwipeStartX] = useState(0);

  const resetTransform = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); setSwipeX(0); }, []);
  const goPrev = useCallback(() => { setIdx(i => (i > 0 ? i - 1 : images.length - 1)); resetTransform(); }, [images.length, resetTransform]);
  const goNext = useCallback(() => { setIdx(i => (i < images.length - 1 ? i + 1 : 0)); resetTransform(); }, [images.length, resetTransform]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") goPrev(); if (e.key === "ArrowRight") goNext(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, goPrev, goNext]);

  const handleWheel = (e) => { e.preventDefault(); setScale(s => Math.max(0.5, Math.min(5, s + (e.deltaY > 0 ? -0.2 : 0.2)))); };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setLastDist(Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY));
    } else if (e.touches.length === 1) {
      if (scale > 1) {
        // Drag mode when zoomed in
        setDragging(true); setStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY }); setStartDrag({ ...pos });
      } else {
        // Swipe mode when at normal scale
        setSwiping(true); setSwipeStartX(e.touches[0].clientX); setSwipeX(0);
      }
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      if (lastDist > 0) setScale(s => Math.max(0.5, Math.min(5, s * (dist / lastDist))));
      setLastDist(dist);
    } else if (e.touches.length === 1) {
      if (dragging && scale > 1) {
        setPos({ x: startDrag.x + (e.touches[0].clientX - startPos.x), y: startDrag.y + (e.touches[0].clientY - startPos.y) });
      } else if (swiping && scale <= 1) {
        setSwipeX(e.touches[0].clientX - swipeStartX);
      }
    }
  };
  const handleTouchEnd = () => {
    setLastDist(0);
    setDragging(false);
    if (swiping) {
      if (swipeX < -60 && images.length > 1) goNext();
      else if (swipeX > 60 && images.length > 1) goPrev();
      setSwiping(false); setSwipeX(0);
    }
  };
  const handleMouseDown = (e) => { if (scale > 1) { setDragging(true); setStartPos({ x: e.clientX, y: e.clientY }); setStartDrag({ ...pos }); } };
  const handleMouseMove = (e) => { if (dragging && scale > 1) setPos({ x: startDrag.x + (e.clientX - startPos.x), y: startDrag.y + (e.clientY - startPos.y) }); };
  const handleMouseUp = () => setDragging(false);

  const img = images[idx];
  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center select-none" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="training-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 z-50 p-2.5 bg-white/15 hover:bg-white/25 rounded-full" data-testid="lightbox-close"><X className="h-6 w-6 text-white" /></button>
      {/* Position indicator */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-1.5 bg-white/15 rounded-full backdrop-blur-sm">
        <span className="text-white text-sm font-bold">{idx + 1} / {images.length}</span>
      </div>
      {images.length > 1 && (<>
        <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/15 hover:bg-white/25 rounded-full" data-testid="lightbox-prev"><ChevronLeft className="h-7 w-7 text-white" /></button>
        <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-3 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/15 hover:bg-white/25 rounded-full" data-testid="lightbox-next"><ChevronRight className="h-7 w-7 text-white" /></button>
      </>)}
      {/* Swipe hint dots */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex gap-2">
          {images.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/30"}`} />)}
        </div>
      )}
      <div className="w-full h-full flex items-center justify-center overflow-hidden" onWheel={handleWheel} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{ cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "default", touchAction: "none" }}>
        <img src={img.url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl pointer-events-none" style={{ transform: `translate(${pos.x + (swiping ? swipeX : 0)}px, ${pos.y}px) scale(${scale})`, transition: (dragging || swiping) ? "none" : "transform 0.25s ease" }} draggable={false} />
      </div>
    </div>
  );
};

// Single-image carousel with swipe
const ImageCarousel = ({ images, onOpenLightbox, isAdmin, onDelete }) => {
  const [current, setCurrent] = useState(0);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [startX, setStartX] = useState(0);

  const goPrev = () => setCurrent(c => (c > 0 ? c - 1 : images.length - 1));
  const goNext = () => setCurrent(c => (c < images.length - 1 ? c + 1 : 0));

  const handleTouchStart = (e) => { setSwiping(true); setStartX(e.touches[0].clientX); setSwipeX(0); };
  const handleTouchMove = (e) => { if (swiping) setSwipeX(e.touches[0].clientX - startX); };
  const handleTouchEnd = () => {
    if (swiping) {
      if (swipeX < -50) goNext();
      else if (swipeX > 50) goPrev();
      setSwiping(false); setSwipeX(0);
    }
  };

  const img = images[current];
  if (!img) return null;

  return (
    <div className="relative" data-testid="training-carousel">
      {/* Image */}
      <div
        className="relative rounded-lg overflow-hidden cursor-pointer bg-gray-100"
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div className="aspect-[4/3]" onClick={() => onOpenLightbox(current)}>
          <img
            src={img.url} alt=""
            className="w-full h-full object-contain"
            style={{ transform: swiping ? `translateX(${swipeX}px)` : "translateX(0)", transition: swiping ? "none" : "transform 0.3s ease" }}
            draggable={false}
            data-testid={`carousel-image-${current}`}
          />
        </div>
        {isAdmin && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(img.id); }} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-full transition-colors" data-testid="carousel-delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      {images.length > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button onClick={goPrev} className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="carousel-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-[#2F5D8A]" data-testid="carousel-indicator">{current + 1} / {images.length}</span>
            <div className="flex gap-1.5">
              {images.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-[#2F5D8A]" : "bg-gray-300"}`} data-testid={`carousel-dot-${i}`} />
              ))}
            </div>
          </div>
          <button onClick={goNext} className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="carousel-next">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

// Internal Inspections Carousel — fixed 4 categories with labels + exterior rules
const INTERNAL_CATEGORIES = [
  { label: "National", rule: "Exterior only NOT allowed", ruleColor: "bg-red-600" },
  { label: "SIFINT / AMICA", rule: "Exterior only NOT allowed", ruleColor: "bg-red-600" },
  { label: "ILIS", rule: "Exterior only NOT allowed", ruleColor: "bg-red-600" },
  { label: "Mapfre / Vermont / Others", rule: "Exterior allowed ONLY with office approval", ruleColor: "bg-amber-600" },
];

const InternalCarousel = ({ photos, onOpenLightbox }) => {
  const [current, setCurrent] = useState(0);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [startX, setStartX] = useState(0);

  const goPrev = () => setCurrent(c => (c > 0 ? c - 1 : photos.length - 1));
  const goNext = () => setCurrent(c => (c < photos.length - 1 ? c + 1 : 0));

  const handleTouchStart = (e) => { setSwiping(true); setStartX(e.touches[0].clientX); setSwipeX(0); };
  const handleTouchMove = (e) => { if (swiping) setSwipeX(e.touches[0].clientX - startX); };
  const handleTouchEnd = () => {
    if (swiping) {
      if (swipeX < -50) goNext();
      else if (swipeX > 50) goPrev();
      setSwiping(false); setSwipeX(0);
    }
  };

  if (!photos.length) return null;
  const cat = INTERNAL_CATEGORIES[current] || INTERNAL_CATEGORIES[0];
  const photo = photos[current];

  return (
    <div className="space-y-2" data-testid="internal-carousel">
      {/* Category label + exterior rule */}
      <div className="text-center space-y-1.5">
        <p className="text-sm font-bold text-[#1F2937]" data-testid="internal-category-label">{cat.label}</p>
        <span className={`inline-block px-3 py-1 rounded-md text-xs font-bold text-white ${cat.ruleColor}`} data-testid="internal-exterior-rule">{cat.rule}</span>
      </div>

      {/* Image */}
      <div
        className="relative rounded-lg overflow-hidden cursor-pointer bg-gray-100"
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div className="aspect-[4/3]" onClick={() => onOpenLightbox(current)}>
          <img src={photo.url} alt="" className="w-full h-full object-contain" style={{ transform: swiping ? `translateX(${swipeX}px)` : "translateX(0)", transition: swiping ? "none" : "transform 0.3s ease" }} draggable={false} />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={goPrev} className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="internal-prev"><ChevronLeft className="h-5 w-5" /></button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-[#2F5D8A]" data-testid="internal-indicator">{current + 1} / {photos.length}</span>
          <div className="flex gap-1.5">
            {photos.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-[#2F5D8A]" : "bg-gray-300"}`} />)}
          </div>
        </div>
        <button onClick={goNext} className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="internal-next"><ChevronRight className="h-5 w-5" /></button>
      </div>
    </div>
  );
};

const TrainingGuide = ({ orderType, isAdmin = false, flowType = "" }) => {
  const [internalPhotos, setInternalPhotos] = useState([]);
  const [externalPhotos, setExternalPhotos] = useState([]);
  const [fintPhotos, setFintPhotos] = useState([]);
  const [regularPhotos, setRegularPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Behavior change (Feb 2026): the training guide is now a button.
  // It starts collapsed so the inspector opens it only when needed — keeps
  // the order screen clean and reduces clutter.
  const [expanded, setExpanded] = useState(false);
  const [lightboxData, setLightboxData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isInternal = (flowType || "").toLowerCase() === "internal";
  const isExternal = (flowType || "").toLowerCase() === "external";
  // Angela 2026-05-10 v3 — FINT vacant-house orders also need a Training
  // Guide section. FINT carries flow_type="Regular" so it isn't internal/
  // external; treat it as its own per-orderType bucket, similar to External.
  const isFINT = (orderType || "").toUpperCase() === "FINT";
  // Angela Feb 2026 — restored per PRD: Regular orders (non-FINT) also need
  // Training Guide, addressed by workcode (orderType). This unlocks the
  // guide for ALL inspector orders — Regular / Internal / External — so
  // inspectors always have a visual reference regardless of flow_type.
  const isRegular = !isInternal && !isExternal && !isFINT && !!orderType;

  const fetchPhotos = useCallback(async () => {
    // Internal: use the generic "INTERNAL" category (Feb 2026 change) which
    // feeds the 4-category InternalCarousel.
    // External: RESTORED (Apr 2026) — load order-type-specific photos
    // (e.g. E12RRD). The backend performs pattern fallback E##RRD → E##,
    // SIFIEX → SIFIEX, etc.
    // FINT: per-orderType bucket "FINT" (Angela 2026-05-10 v3).
    if (isInternal) {
      try {
        const r = await axios.get(`${API}/training-photos/INTERNAL`);
        setInternalPhotos(r.data || []);
      } catch { setInternalPhotos([]); }
    } else if (isExternal && orderType) {
      try {
        const r = await axios.get(`${API}/training-photos/${encodeURIComponent(orderType)}`);
        setExternalPhotos(r.data || []);
      } catch { setExternalPhotos([]); }
    } else if (isFINT) {
      try {
        const r = await axios.get(`${API}/training-photos/FINT`);
        setFintPhotos(r.data || []);
      } catch { setFintPhotos([]); }
    } else if (isRegular) {
      // Regular orders — bucket keyed by workcode (orderType). Backend
      // handles pattern fallback like External (e.g., SIFXX → SIFXX).
      try {
        const r = await axios.get(`${API}/training-photos/${encodeURIComponent(orderType)}`);
        setRegularPhotos(r.data || []);
      } catch { setRegularPhotos([]); }
    }
    setLoading(false);
  }, [isInternal, isExternal, isFINT, isRegular, orderType]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    // Internal uploads go into the generic INTERNAL bucket; External uploads
    // go into the per-order-type bucket so the pattern fallback in the
    // backend can serve them for any matching variant (E12RRD, E12RNN, ...).
    // FINT uploads go into the dedicated "FINT" bucket.
    let uploadCategory;
    if (isFINT) uploadCategory = "FINT";
    else if (isExternal) uploadCategory = (orderType || "INTERNAL");
    else if (isRegular) uploadCategory = (orderType || "INTERNAL");
    else uploadCategory = "INTERNAL";
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      try {
        await axios.post(`${API}/training-photos/${encodeURIComponent(uploadCategory)}`, fd);
      } catch (err) { console.error("Upload failed:", err); }
    }
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
    fetchPhotos();
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Delete this training photo?")) return;
    try { await axios.delete(`${API}/training-photos/${photoId}`); fetchPhotos(); }
    catch (err) { console.error("Delete failed:", err); }
  };

  if (loading) return null;
  // Angela Feb 2026 (PRD Inspector App): the guide is now visible on
  // Regular orders as well (per-workcode bucket), so we only return null
  // when the order has NO workcode at all — nothing meaningful to key on.
  if (!isInternal && !isExternal && !isFINT && !isRegular) return null;
  // Restore policy (Angela 2026-05-15): inspector view ALWAYS keeps the
  // Training Guide toggle visible — even when photos haven't been
  // uploaded yet. Previously the component returned null on zero photos,
  // making the button silently disappear and inspectors thought the
  // guide was removed. The expanded panel handles the empty case
  // gracefully (see "no photos yet" empty states below). The admin
  // training-management screen is unaffected because it passes
  // isAdmin=true. FINT is unchanged.
  const currentPhotos = isInternal
    ? internalPhotos
    : (isFINT ? fintPhotos : (isRegular ? regularPhotos : externalPhotos));

  const code = (orderType || "").toUpperCase();

  return (
    <>
      <div className="border border-[#2F5D8A]/20 rounded-lg overflow-hidden" data-testid="training-guide-section">
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 bg-[#2F5D8A]/5 hover:bg-[#2F5D8A]/10 transition-colors" data-testid="training-guide-toggle">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-[#2F5D8A]" />
            <span className="text-sm font-bold text-[#2F5D8A] uppercase tracking-wide">Training Guide</span>
            {code && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2F5D8A]/10 text-[#2F5D8A] font-semibold">{code}</span>}
            {currentPhotos.length > 0 && <span className="text-[10px] text-gray-400">{currentPhotos.length} {isInternal ? 'slides' : 'photos'}</span>}
            {!expanded && <span className="text-[10px] text-[#2F5D8A] font-semibold">· click to open</span>}
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-[#2F5D8A]" /> : <ChevronDown className="h-4 w-4 text-[#2F5D8A]" />}
        </button>

        {expanded && (
          <div className="px-4 py-3 space-y-3">
            {/* Reference-only warning */}
            {currentPhotos.length > 0 && (
              <div className="bg-red-50 border border-red-300 rounded-lg px-3 py-2.5" data-testid="training-warning">
                <p className="text-sm text-red-700 font-bold leading-relaxed">
                  This guide is for reference only. Always check the app requirements for each order, as additional photos may be required.
                </p>
              </div>
            )}

            {/* Admin upload bar */}
            {isAdmin && (
              <div className="flex items-center gap-2" data-testid="training-upload-bar">
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="training-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors" data-testid="training-upload-btn">
                  <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Upload Photos"}
                </button>
                <span className="text-[10px] text-gray-400">{isFINT ? "Uploads into the FINT guide category" : ((isExternal || isRegular) ? `Uploads into the ${code} guide category` : "Uploads into the INTERNAL guide category")}</span>
              </div>
            )}

            {/* Internal: 4-category carousel. External: per-order-type photo carousel. */}
            {isInternal && internalPhotos.length > 0 && (
              <div data-testid="internal-inspections-section">
                <p className="text-xs font-bold text-[#2F5D8A] uppercase tracking-wider mb-3">Internal Inspections</p>
                <InternalCarousel
                  photos={internalPhotos.map(p => ({ url: `${BASE}${p.url}`, id: p.id }))}
                  onOpenLightbox={(idx) => setLightboxData({ images: internalPhotos.map(p => ({ url: `${BASE}${p.url}` })), index: idx })}
                />
              </div>
            )}

            {isExternal && externalPhotos.length > 0 && (
              <div data-testid="external-inspections-section">
                <p className="text-xs font-bold text-[#2F5D8A] uppercase tracking-wider mb-3">External Reference Photos</p>
                <ImageCarousel
                  images={externalPhotos.map(p => ({ url: `${BASE}${p.url}`, id: p.id }))}
                  onOpenLightbox={(idx) => setLightboxData({ images: externalPhotos.map(p => ({ url: `${BASE}${p.url}` })), index: idx })}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {isFINT && fintPhotos.length > 0 && (
              <div data-testid="fint-inspections-section">
                <p className="text-xs font-bold text-[#2F5D8A] uppercase tracking-wider mb-3">FINT Reference Photos</p>
                <ImageCarousel
                  images={fintPhotos.map(p => ({ url: `${BASE}${p.url}`, id: p.id }))}
                  onOpenLightbox={(idx) => setLightboxData({ images: fintPhotos.map(p => ({ url: `${BASE}${p.url}` })), index: idx })}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {isRegular && regularPhotos.length > 0 && (
              <div data-testid="regular-inspections-section">
                <p className="text-xs font-bold text-[#2F5D8A] uppercase tracking-wider mb-3">Reference Photos · {code}</p>
                <ImageCarousel
                  images={regularPhotos.map(p => ({ url: `${BASE}${p.url}`, id: p.id }))}
                  onOpenLightbox={(idx) => setLightboxData({ images: regularPhotos.map(p => ({ url: `${BASE}${p.url}` })), index: idx })}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {currentPhotos.length === 0 && (
              <div className="text-center py-6 text-gray-400" data-testid="training-no-photos">
                <Camera className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-xs">No training photos uploaded yet.</p>
                {isAdmin && <p className="text-[10px] mt-1">Use the button above to upload reference photos.</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {lightboxData && (
        <Lightbox images={lightboxData.images} currentIndex={lightboxData.index} onClose={() => setLightboxData(null)} />
      )}
    </>
  );
};

export default TrainingGuide;
